import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Card, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { filter, find, includes, isNil, map, values } from "lodash";
import { message } from "components";
import { AICC_LANGUAGE_STATUS, AiccLanguageStatus } from "constants/aiccLanguage";
import { AICC_PROMPT_RESET_TARGET, AiccPromptResetTarget, aiccPromptGroupFromSlug } from "constants/aiccPrompt";
import { AiccPromptConfig, AiccPromptVariable } from "types/model/aiccPrompt";
import * as aiccPromptAPI from "services/aiccPrompt";
import { useLearningAssetsStore } from "../../useLearningAssetsStore";
import { useAiccManagedLanguageNames } from "../../LearningAssets/hooks/useAiccManagedLanguageNames";
import { useAiccPrompt } from "../../hooks/useAiccPrompt";
import { useInvalidateAiccPrompts } from "../../hooks/useAiccPrompts";
import { useAiccPromptModels } from "../../hooks/useAiccPromptModels";
import { confirmSaveLearningAsset } from "../../LearningAssets/confirmations";
import DefaultReferencePanel from "./Reference/DefaultReferencePanel";
import CompareModal from "./Reference/CompareModal";
import ModelSettings from "./ModelSettings";
import ReadOnlySettingsBar from "./ModelSettings/ReadOnlySettingsBar";
import ValidationFooter from "./ValidationFooter";
import PromptEditor, { PromptEditorHandle } from "../PromptEditor";
import { AICC_PROMPT_STATUS_PARAM } from "../paths";
import VariableDictionary from "./Reference/VariableDictionary";
import LanguageStatusTag from "../LanguageStatusTag";
import { useSandboxSavesStore } from "../Sandbox/useSandboxSavesStore";
import { promptDraftHealth } from "./ValidationFooter/draft";
import { changeModel, saveRequest } from "./draftChanges";
import { promptSettingChanges, promptTextDiff, promptTextMatches } from "./promptDiff";
import { useUnsavedChangesGuard } from "./useUnsavedChangesGuard";
import { confirmDiscardDraftChanges, confirmResetPrompt } from "./confirmations";
import {
  DEFAULT_REFERENCE_DESCRIPTION,
  DEFAULT_REFERENCE_TITLE,
  DISCARD_SUCCESS,
  INHERITED_SETTING,
  PROMPT_LOADING,
  PROMPT_NOT_FOUND,
  PUBLISHED_REFERENCE_DESCRIPTION,
  PUBLISHED_REFERENCE_TITLE,
  RESET_PUBLIC_SUCCESS,
  RESET_SUCCESS,
  SAVE_SUCCESS,
  SAVE_WRITES_TO_DRAFT,
} from "./configs";
import styles from "./PromptDetail.module.scss";

const EMPTY_DRAFT: AiccPromptConfig = { promptText: "" };

function PromptDetail() {
  const { group: groupSlug, promptKey } = useParams<{ group: string; promptKey: string }>();
  const { search } = useLocation();
  const languageId = useLearningAssetsStore((state) => state.languageId);
  const setLanguageId = useLearningAssetsStore((state) => state.setLanguageId);
  const activeStatus = useLearningAssetsStore((state) => state.activeStatus);

  const statusInLink = new URLSearchParams(search).get(AICC_PROMPT_STATUS_PARAM);
  const knownStatusInLink = includes(values(AICC_LANGUAGE_STATUS), statusInLink)
    ? (statusInLink as AiccLanguageStatus)
    : undefined;
  const openedStatus = isNil(statusInLink) ? activeStatus : knownStatusInLink;
  const isEditable = !!openedStatus && openedStatus !== AICC_LANGUAGE_STATUS.public;

  const { languages } = useAiccManagedLanguageNames();

  useEffect(() => {
    if (!languages[0] || find(languages, { id: languageId })) {
      return;
    }
    setLanguageId(languages[0].id);
  }, [languageId, languages, setLanguageId]);

  const { prompt, isLoading, isError, mutate } = useAiccPrompt(languageId, promptKey, openedStatus);
  const invalidatePromptLists = useInvalidateAiccPrompts();
  const languageName = find(languages, { id: languageId })?.name;
  const { models, modelsById, defaultModel } = useAiccPromptModels();

  const displayedStatus = prompt?.status ?? openedStatus;

  const editorRef = useRef<PromptEditorHandle>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [resettingTarget, setResettingTarget] = useState<AiccPromptResetTarget>();
  const [isCompareVisible, setIsCompareVisible] = useState(false);

  const [draft, setDraft] = useState<AiccPromptConfig>(EMPTY_DRAFT);
  const announceSave = useSandboxSavesStore((state) => state.announceSave);

  useEffect(() => {
    setDraft(prompt?.workingConfig ?? EMPTY_DRAFT);
  }, [prompt]);

  const saved = prompt?.workingConfig ?? EMPTY_DRAFT;

  const health = promptDraftHealth(draft, saved, prompt?.variables ?? []);

  useUnsavedChangesGuard(health.isDirty && !isSaving && !resettingTarget);

  const reference = prompt?.publishedConfig ?? prompt?.defaultConfig;

  const diff = useMemo(
    () => (reference && isEditable ? promptTextDiff(reference.promptText, draft.promptText) : undefined),
    [reference, isEditable, draft.promptText]
  );

  const settingChanges = useMemo(
    () =>
      reference && isEditable
        ? promptSettingChanges(reference, draft, {
            modelName: (id) => (id ? modelsById[id]?.name : undefined),
            inherited: INHERITED_SETTING,
          })
        : [],
    [reference, isEditable, draft, modelsById]
  );

  const handleSave = () => {
    if (!prompt || !languageId) {
      return;
    }

    confirmSaveLearningAsset(openedStatus ?? AICC_LANGUAGE_STATUS.previewReady).then((confirmed) => {
      if (!confirmed) {
        return;
      }

      setIsSaving(true);
      aiccPromptAPI
        .saveAiccPrompt(languageId, prompt.key, saveRequest(draft, saved))
        .then((response) => {
          message.success(SAVE_SUCCESS(response.name));
          announceSave({
            languageId,
            promptKey: prompt.key,
            name: response.name,
            group: aiccPromptGroupFromSlug(groupSlug),
          });
          invalidatePromptLists();
          return mutate(response, { revalidate: false });
        })
        .catch(() => mutate())
        .finally(() => setIsSaving(false));
    });
  };

  const handleReset = (target: AiccPromptResetTarget) => {
    if (!prompt || !languageId) {
      return;
    }

    confirmResetPrompt(target, openedStatus).then((confirmed) => {
      if (!confirmed) {
        return;
      }

      setResettingTarget(target);
      aiccPromptAPI
        .resetAiccPrompt(languageId, prompt.key, target)
        .then((response) => {
          message.success(
            target === AICC_PROMPT_RESET_TARGET.public
              ? RESET_PUBLIC_SUCCESS(response.name)
              : RESET_SUCCESS(response.name)
          );
          announceSave({
            languageId,
            promptKey: prompt.key,
            name: response.name,
            group: aiccPromptGroupFromSlug(groupSlug),
          });
          invalidatePromptLists();
          return mutate(response, { revalidate: false });
        })
        .catch(() => mutate())
        .finally(() => setResettingTarget(undefined));
    });
  };

  const handleDiscard = () => {
    confirmDiscardDraftChanges().then((confirmed) => {
      if (!confirmed) {
        return;
      }

      setDraft(saved);
      message.success(DISCARD_SUCCESS);
    });
  };

  if (isLoading || isNil(languageId)) {
    return (
      <Card title="Prompt" className="page-container">
        <div className={styles.loading}>
          <Spin size="small" indicator={<LoadingOutlined spin />} />
          {PROMPT_LOADING}
        </div>
      </Card>
    );
  }

  if (isError || !prompt) {
    return (
      <Card title="Prompt" className="page-container">
        <div className={styles.loading}>{PROMPT_NOT_FOUND}</div>
      </Card>
    );
  }

  const readOnlyConfig = prompt.publishedConfig ?? prompt.defaultConfig;
  const isReferencePublished = !!prompt.publishedConfig && reference === prompt.publishedConfig;
  const referenceTitle = isReferencePublished ? PUBLISHED_REFERENCE_TITLE : DEFAULT_REFERENCE_TITLE;
  const rules = {
    requiredNames: map(filter(prompt.variables, "isRequired"), "name"),
    knownNames: map(prompt.variables, "name"),
  };
  const resets = {
    [AICC_PROMPT_RESET_TARGET.default]: {
      isOffered: !!prompt.defaultConfig,
      isRestorable: !!prompt.defaultConfig && !promptTextMatches(prompt.defaultConfig.promptText, draft.promptText),
    },
    [AICC_PROMPT_RESET_TARGET.public]: {
      isOffered: !!prompt.publishedConfig,
      isRestorable: !!prompt.publishedConfig && !promptTextMatches(prompt.publishedConfig.promptText, draft.promptText),
    },
  };

  return (
    <Card
      title={
        <div className={styles.pageTitle}>
          <span>{prompt.name}</span>
          <LanguageStatusTag languageName={languageName} status={displayedStatus} />
        </div>
      }
      className="page-container"
    >
      <div className={styles.workspace}>
        <div className={styles.referenceColumn}>
          <DefaultReferencePanel
            title={referenceTitle}
            description={isReferencePublished ? PUBLISHED_REFERENCE_DESCRIPTION : DEFAULT_REFERENCE_DESCRIPTION}
            config={reference}
            modelName={reference?.modelId ? modelsById[reference.modelId]?.name : undefined}
            isDeprecated={!!reference?.modelId && !!modelsById[reference.modelId]?.isDeprecated}
            shutdownAt={reference?.modelId ? modelsById[reference.modelId]?.shutdownAt : undefined}
            diff={diff}
            settingChanges={settingChanges}
            onView={reference ? () => setIsCompareVisible(true) : undefined}
            onDiscard={isEditable && health.isDirty ? handleDiscard : undefined}
          />
          <VariableDictionary
            variables={prompt.variables}
            onInsert={
              isEditable ? ({ name }: AiccPromptVariable) => editorRef.current?.insertVariable(name) : undefined
            }
          />
        </div>

        <div className={styles.editorColumn}>
          {isEditable && <div className={styles.scopeBar}>{SAVE_WRITES_TO_DRAFT}</div>}

          {isEditable ? (
            <ModelSettings
              models={models}
              modelId={draft.modelId ?? defaultModel?.id}
              temperature={draft.temperature}
              reasoningEffort={draft.reasoningEffort}
              sharesModelWith={prompt.sharesModelWith}
              onModelChange={(modelId) => setDraft((current) => changeModel(current, models, modelId))}
              onTemperatureChange={(temperature) => setDraft((current) => ({ ...current, temperature }))}
              onEffortChange={(reasoningEffort) => setDraft((current) => ({ ...current, reasoningEffort }))}
            />
          ) : (
            readOnlyConfig && (
              <ReadOnlySettingsBar
                config={readOnlyConfig}
                modelName={readOnlyConfig.modelId ? modelsById[readOnlyConfig.modelId]?.name : undefined}
                isDeprecated={!!readOnlyConfig.modelId && !!modelsById[readOnlyConfig.modelId]?.isDeprecated}
                deprecationNote={
                  readOnlyConfig.modelId ? modelsById[readOnlyConfig.modelId]?.deprecationNote : undefined
                }
                shutdownAt={readOnlyConfig.modelId ? modelsById[readOnlyConfig.modelId]?.shutdownAt : undefined}
              />
            )
          )}

          <div className={styles.editorSlot}>
            <PromptEditor
              ref={editorRef}
              value={isEditable ? draft.promptText : readOnlyConfig?.promptText ?? ""}
              rules={rules}
              readOnly={!isEditable}
              onChange={(promptText) => isEditable && setDraft((current) => ({ ...current, promptText }))}
            />
          </div>

          {isEditable && (
            <ValidationFooter
              health={health}
              resets={resets}
              isSaving={isSaving}
              resettingTarget={resettingTarget}
              onSave={handleSave}
              onReset={handleReset}
            />
          )}
        </div>
      </div>

      <CompareModal
        visible={isCompareVisible}
        referenceTitle={referenceTitle}
        referenceText={reference?.promptText ?? ""}
        rules={rules}
        diff={diff}
        settingChanges={settingChanges}
        onClose={() => setIsCompareVisible(false)}
      />
    </Card>
  );
}

export default PromptDetail;
